import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { captureUtmFromUrl, getCurrentUtm } from '@/lib/utmTracking';
import { pageViewFields } from '@/lib/privatePaths';

/**
 * GA4 (Google Analytics 4) integration — loads gtag.js on first mount
 * when VITE_GA4_MEASUREMENT_ID is set, and emits a `page_view` event on
 * every react-router navigation.
 *
 * Why not rely on the GTM container that's already in index.html?
 *   - GTM is a *delivery channel*; whether GA4 actually fires depends
 *     on a tag being configured inside the GTM dashboard. That can be
 *     edited by anyone with GTM access and silently broken.
 *   - Direct gtag.js gives us a deterministic, in-repo wire: if the
 *     env var is set, GA4 is active. No GTM round-trip.
 *   - Both can coexist — GTM continues to fire whatever else is
 *     configured (Meta Pixel, etc.); this hook just guarantees GA4.
 *
 * SPA caveat: GA4's "Enhanced measurement" page_view auto-tracking only
 * fires on initial load, not on react-router pushState navigations. We
 * disable that automatic page_view (`send_page_view: false` in the
 * config) and emit our own on `pathname` change so the realtime view
 * accurately reflects route changes.
 *
 * Free tier: 10 million events/month per property. More than enough
 * even for the heaviest launch-day traffic — no card on file needed.
 *
 * Setup:
 *   1. Create a GA4 property at analytics.google.com (Free).
 *   2. Copy the Measurement ID (format G-XXXXXXXXXX).
 *   3. Set VITE_GA4_MEASUREMENT_ID in your environment / Vercel.
 *   4. Redeploy. Visit any page; check the GA4 Realtime report.
 *
 * Unset (or empty) env var → hook is a no-op. Safe to leave in code.
 */
// Baked-in default: Fly's own GA4 property Measurement ID. A GA4
// Measurement ID is NOT a secret — it ships as the query string of
// every `gtag/js?id=...` request the browser makes, visible to
// anyone with devtools. Keeping it in code (not in a Vercel env var)
// lets us wire tracking without the owner needing to touch Vercel
// permissions. The env var still wins when set, so a preview deploy
// or a fork can override to point at a different property.
const GA4_ID_DEFAULT = 'G-ZPK88QZPR5';
const GA4_ID = (import.meta.env.VITE_GA4_MEASUREMENT_ID || GA4_ID_DEFAULT).trim();
const GA4_ID_PATTERN = /^G-[A-Z0-9]{4,15}$/;

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

let scriptInjected = false;

function injectGtagScript(measurementId: string) {
  if (scriptInjected || typeof document === 'undefined') return;
  scriptInjected = true;

  // The shim and the queued events happen NOW, so nothing is lost. Only the
  // download waits: index.html holds every tag vendor back until the visitor
  // interacts or the page has settled, and fetching gtag.js here at mount
  // would pull that same vendor in early and undo it. One scheduler, in
  // index.html, rather than a second one here.
  const fetchGtag = () => {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(s);
  };
  const w = window as Window & { __flyTagsLoaded?: boolean };
  if (w.__flyTagsLoaded) fetchGtag();
  else window.addEventListener('fly:tags-ready', fetchGtag, { once: true });

  window.dataLayer = window.dataLayer || [];
  // gtag is a thin shim that pushes args into dataLayer; gtag.js picks
  // them up and forwards to Google. Don't rewrite as an arrow function —
  // gtag.js inspects `arguments` to read variadic args.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  } as unknown as typeof window.gtag;

  window.gtag?.('js', new Date());
  // send_page_view:false → we emit page_view ourselves on route change
  // (SPA navigations don't trigger gtag's auto-pageview).
  window.gtag?.('config', measurementId, {
    send_page_view: false,
    anonymize_ip: true,
  });
}

export function useGA4(): { enabled: boolean; measurementId: string } {
  const { pathname, search } = useLocation();
  const isValidId = GA4_ID_PATTERN.test(GA4_ID);
  const lastPath = useRef<string | null>(null);
  const lastUtmPath = useRef<string | null>(null);

  // Inject the script exactly once on first mount when configured.
  useEffect(() => {
    if (!isValidId) return;
    injectGtagScript(GA4_ID);
  }, [isValidId]);

  // Run UTM capture on EVERY route change, regardless of whether GA4 is
  // configured. This is the keystone for attribution across the whole
  // funnel — trackEvent + trackGoogleEvent both pull from
  // getCurrentUtm() when writing into user_events / GA4. Gating UTM
  // capture behind GA4 means dev environments (no VITE_GA4_MEASUREMENT_ID)
  // and any prod deploy with a misconfigured GA4 id would silently
  // produce empty utm_* in every event row, making the Live Ops feed's
  // attribution column read "unknown" for traffic that DID arrive with
  // tagged URLs.
  useEffect(() => {
    const fullPath = pathname + search;
    if (lastUtmPath.current === fullPath) return;
    lastUtmPath.current = fullPath;
    captureUtmFromUrl();
  }, [pathname, search]);

  // Emit page_view on every route change. Coalesces back-to-back
  // identical paths (e.g. React strict-mode double-renders) so the
  // realtime view doesn't double-count.
  //
  // A private agent page goes out as its route template (/agent/home/:token,
  // /app/l/:location/home) and never sends its query string or hash: an owner
  // link's token is a bearer credential (src/lib/privatePaths.ts). Every other
  // page, the GMB tool's included, sends exactly what it sent before:
  // pathname plus search, and window.location.href.
  useEffect(() => {
    if (!isValidId) return;
    const fullPath = pathname + search;
    if (lastPath.current === fullPath) return;
    lastPath.current = fullPath;
    const utm = getCurrentUtm();
    const { page_path, page_location } = pageViewFields({
      pathname,
      search,
      href: window.location.href,
      origin: window.location.origin,
    });
    window.gtag?.('event', 'page_view', {
      page_path,
      page_location,
      page_title: typeof document !== 'undefined' ? document.title : undefined,
      ...utm,
    });
  }, [isValidId, pathname, search]);

  return { enabled: isValidId, measurementId: GA4_ID };
}

/**
 * Drop-in component that runs `useGA4` and renders nothing. Lets us
 * keep `App.tsx` clean: just `<GA4PageView />` alongside `<ScrollToTop />`
 * inside the Routes shell.
 */
export function GA4PageView() {
  useGA4();
  return null;
}
